import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { ConversationModel } from '~/models';
import { BulkPaneShell } from '../conversation/BulkPaneShell';
import { greetingScopeKey } from '../conversation/greetingScopeKey';
import { bulkAmendmentLaneConversationApi } from '../conversation/laneConversationApis';
import { useBulkAgentPane } from '../conversation/useBulkAgentPane';
import { useConversationApi } from '../conversation/useConversationApi';
import { assistantEnvelopeUpdates } from './utils/assistantEnvelopeUpdates';
import { latestUserInstruction } from './utils/latestUserInstruction';
export const GetBulkAmendmentAgentPaneTID = (parentTestId)=>getTestIds(parentTestId);
export const BulkAmendmentAgentPane = (param)=>{
    let { scope, agreementNames, onFilterUpdate, onSelectionUpdate, onScopeRestore, onChatCleared, onSellerPrompted, onAgreementsChanged, isOpen, onOpenChange, regreetDelayMs, testId = '' } = param;
    const { t } = useLocaleKeys();
    const { BulkAmendmentAgentPaneTestIds } = useMemo(()=>GetBulkAmendmentAgentPaneTID(testId), [
        testId
    ]);
    const replayed = useRef({
        filter: false,
        selection: false
    });
    const { showErrorToast } = useOverlayContext();
    const page = {
        type: 'bulk-amendment',
        scope
    };
    const api = useConversationApi({
        laneApi: (storeApi)=>bulkAmendmentLaneConversationApi(storeApi, scope, agreementNames),
        page,
        createPage: {
            type: 'bulk-amendment',
            scope,
            agreementNames,
            matchedAgreementCount: scope.matchedAgreementCount,
            selectedAgreementIds: scope.selectedAgreementIds,
            currentFilter: scope.currentFilter
        }
    });
    const { conversation, handlers, sendPrompt, lastInstruction } = useBulkAgentPane(_object_spread_props(_object_spread(_object_spread_props(_object_spread({
        api,
        onScopeRestored: (restoredScope)=>onScopeRestore === null || onScopeRestore === void 0 ? void 0 : onScopeRestore({
                currentFilter: replayed.current.filter ? null : restoredScope.currentFilter,
                selectedAgreementIds: replayed.current.selection ? null : restoredScope.selectedAgreementIds,
                filterReplayed: replayed.current.filter
            }),
        onAssistantMessage: handleAssistantMessage,
        onHydrated: applyPersistedUpdates,
        onUndoUnavailable: ()=>showErrorToast(t.ai.drafting.errors.undoUnavailable(), BulkAmendmentAgentPaneTestIds.UndoUnavailableToast()),
        isOpen
    }, onSellerPrompted != null ? {
        onSellerPrompted
    } : {}), {
        feedbackToastTestId: BulkAmendmentAgentPaneTestIds.FeedbackSuccessToast()
    }), regreetDelayMs != null ? {
        regreetDelayMs
    } : {}), {
        greetingScopeKey: greetingScopeKey({
            selectedIds: scope.selectedAgreementIds,
            matchedCount: scope.matchedAgreementCount,
            currentFilter: scope.currentFilter
        })
    }));
    function applyPersistedUpdates(messages) {
        const restored = ConversationModel.ofMessages(messages).forPage(page);
        const filterUpdate = restored.latestPersistedAgreementFilterUpdate();
        if (filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(filterUpdate, latestUserInstruction(messages));
        const selectionUpdate = restored.latestPersistedSelectionUpdate();
        if (selectionUpdate != null && selectionUpdate.length > 0) onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(selectionUpdate);
        replayed.current = {
            filter: filterUpdate != null,
            selection: selectionUpdate != null && selectionUpdate.length > 0
        };
    }
    function handleAssistantMessage(message) {
        if (!message.belongsToPage(page)) return;
        const updates = assistantEnvelopeUpdates(message);
        if (updates.filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(updates.filterUpdate, lastInstruction());
        if (updates.selectionUpdate != null) onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(updates.selectionUpdate);
        if (updates.changedAgreementIds != null) onAgreementsChanged === null || onAgreementsChanged === void 0 ? void 0 : onAgreementsChanged(updates.changedAgreementIds);
    }
    return /*#__PURE__*/ _jsx(BulkPaneShell, {
        testId: BulkAmendmentAgentPaneTestIds.OpenPaneHeader(),
        isOpen: isOpen,
        conversation: conversation,
        handlers: handlers,
        onSubmitPrompt: sendPrompt,
        clearChatBiName: "clear_bulk_amendment_agent_chat",
        onChatCleared: onChatCleared,
        toggleBiName: "toggle_bulk_amendment_agent_pane",
        onToggle: ()=>onOpenChange(!isOpen),
        onExpand: ()=>onOpenChange(true)
    });
};
